import { ChevronRight, Eye, ShieldCheck, Trophy } from "lucide-solid";
import { For, Show, createSignal, onCleanup, onMount, type Component } from "solid-js";
import { TIERS, formatCategory } from "system";
import { InstallButton } from "~/components/install-button";
import { TierBadge } from "~/components/tier-badge";
import { Button } from "~/components/ui/button";
import { landingApi } from "~/lib/api";
import { useAuth } from "~/lib/auth";
import { FEATURES, firstEnabledRoute } from "~/lib/features";
import { QueryKey } from "~/lib/query-keys";
import { headerLogoUrl, logoUrl } from "~/lib/svg-assets";

import { createQuery } from "@tanstack/solid-query";
import { createFileRoute, useNavigate } from "@tanstack/solid-router";

const SLASH_LEFT_STYLE = { background: "repeating-linear-gradient(-45deg,transparent,transparent 60px,var(--color-brand) 60px,var(--color-brand) 61px)" } as const;
const SLASH_RIGHT_STYLE = { background: "repeating-linear-gradient(45deg,transparent,transparent 80px,var(--color-brand) 80px,var(--color-brand) 81px)" } as const;
const TACH_SWEEP_STYLE = { "animation-delay": "0.5s", width: "0%" } as const;
const TAGLINE_DELAY_STYLE = { "animation-delay": "0.35s" } as const;
const CTA_DELAY_STYLE = { "animation-delay": "0.55s" } as const;
const STATS_DELAY_STYLE = { "animation-delay": "0.7s" } as const;

export const Route = createFileRoute("/")({
  // beforeLoad: ({ context }) => {
  //   if (!context.auth.loading && context.auth.user) {
  //     const to = firstEnabledRoute(context.auth.user.id);
  //     if (to) throw redirect({ to });
  //   }
  // },
  component: LandingPage,
});

/* Compact relative time for the landing pulse: "2h ago", "in 3d", "now".
   Intl's narrow style emits exactly that compact form and owns direction,
   pluralization, and the "now"/"yesterday"/"tomorrow" niceties; we only pick the
   coarse unit, since RelativeTimeFormat formats a given unit but won't choose one.
   Built once at module scope and reused. Stays correct on a cached page since
   it's derived from the ISO timestamp client-side. */
const PULSE_TIME_FMT = new Intl.RelativeTimeFormat("en", { numeric: "auto", style: "narrow" });
function relTime(iso: string): string {
  const sec = Math.round((new Date(iso).getTime() - Date.now()) / 1000);
  const abs = Math.abs(sec);
  if (abs < 60) return PULSE_TIME_FMT.format(sec, "second");
  if (abs < 3600) return PULSE_TIME_FMT.format(Math.round(sec / 60), "minute");
  if (abs < 86_400) return PULSE_TIME_FMT.format(Math.round(sec / 3600), "hour");
  return PULSE_TIME_FMT.format(Math.round(sec / 86_400), "day");
}

const STEPS = [
  {
    n: "1",
    title: "Challenge",
    desc: "Find a peer at your tier or a driver above you and send the call. They have 48 hours to answer, and ducking a fair challenge has a cost.",
  },
  {
    n: "2",
    title: "Race",
    desc: "Best-of series, first to a three-win lead. You pick the tracks, trade races, and grind it out one by one.",
  },
  {
    n: "3",
    title: "Climb",
    desc: "Win and climb the tier ladder. Lose and defend your position from whoever's hungry below you. Every rank is earned.",
  },
];

// The differentiator: each card answers one of the live complaints about online
// FH6 racing (cheating, exploit tunes, no fair skill matching).
const WHY = [
  {
    icon: Eye,
    title: "Every race is judged",
    desc: "Duels and tournaments are streamed to a judge and recorded. Hacking gets you banned instantly, with the footage to prove it.",
  },
  {
    icon: ShieldCheck,
    title: "Fair cars only",
    desc: "Locked car classes and community-tested tunes, so every car is clean and matched to the race.",
  },
  {
    icon: Trophy,
    title: "Earned on skill",
    desc: "Skill is the only thing that moves you up. Challenge your rivals, beat them, and move up the ladder.",
  },
];

/* Random speed lines that streak across the hero */
function SpeedLines() {
  const lines = Array.from({ length: 12 }, () => {
    const width = 60 + Math.random() * 200;
    // Larger width = faster (min 1.6s for smallest, max 4.0s for largest)
    // Invert so: duration = max - (width - minWidth) / (maxWidth - minWidth) * (max - min)
    const minWidth = 60,
      maxWidth = 260;
    const minDur = 4.0,
      maxDur = 1.6;
    const duration =
      maxDur +
      (minDur - maxDur) * (1 - (width - minWidth) / (maxWidth - minWidth));
    return {
      top: `${Math.random() * 100}%`,
      delay: `${Math.random() * 4}s`,
      duration: `${duration}s`,
      width: `${width}px`,
      opacity: 0.15 + Math.random() * 0.3,
    };
  });

  return (
    <div class="absolute inset-0 overflow-hidden pointer-events-none">
      <For each={lines}>
        {(l) => (
          <div
            class="absolute h-[1px] animate-speed-line"
            style={{
              top: l.top,
              left: "0",
              width: l.width,
              "animation-delay": l.delay,
              "animation-duration": l.duration,
              "animation-iteration-count": "infinite",
              background: `linear-gradient(90deg, transparent, var(--color-brand), transparent)`,
              opacity: l.opacity,
            }}
          />
        )}
      </For>
    </div>
  );
}

/* Horizontal light streaks in the tier strip */
function TierStreaks() {
  const streaks = Array.from({ length: 5 }, (_, i) => {
    const width = 40 + Math.random() * 120;
    // Larger width = faster, smaller = slower (min 5.0s, max 2.0s)
    const minWidth = 40,
      maxWidth = 160;
    const minDur = 5.0,
      maxDur = 2.0;
    const duration =
      maxDur +
      (minDur - maxDur) * (1 - (width - minWidth) / (maxWidth - minWidth));
    return {
      top: `${10 + Math.random() * 80}%`,
      delay: `${i * 0.8 + Math.random() * 2}s`,
      duration: `${duration}s`,
      width: `${width}px`,
      color: TIERS[Math.floor(Math.random() * TIERS.length)],
    };
  });

  return (
    <For each={streaks}>
      {(s) => (
        <div
          class="absolute h-[1px] animate-streak"
          style={{
            top: s.top,
            left: "0",
            width: s.width,
            "animation-delay": s.delay,
            "animation-duration": s.duration,
            "animation-iteration-count": "infinite",
            background: `linear-gradient(90deg, transparent, var(--color-tier-${s.color}), transparent)`,
            opacity: "0.4",
          }}
        />
      )}
    </For>
  );
}

// Pulsing "live" indicator for the scene ticker.
function LiveDot() {
  return (
    <span class="relative inline-flex h-2 w-2">
      <span class="absolute inline-flex h-full w-full rounded-full bg-brand opacity-75 animate-ping" />
      <span class="relative inline-flex h-2 w-2 rounded-full bg-brand" />
    </span>
  );
}

// Join is the primary door for newcomers (Login presumes you're already a
// member, which a stranger isn't). Login drops to a small secondary link.
// Reused verbatim by the hero and the footer CTA.
function CtaButtons() {
  const { state, signIn } = useAuth();
  const navigate = useNavigate();
  const inviteUrl = import.meta.env.VITE_DISCORD_INVITE_URL as string | undefined;

  return (
    <div class="flex gap-3 items-center justify-center">
      <Show
        when={!state.loading}
        fallback={
          <Button size="lg" disabled>
            Loading...
          </Button>
        }
      >
        <Show
          when={state.user}
          fallback={
            <>
              <Show
                when={inviteUrl}
                fallback={
                  <Show when={FEATURES.auth}>
                    <Button size="lg" onClick={signIn} class="whitespace-nowrap">
                      Login with Discord <ChevronRight size={18} />
                    </Button>
                  </Show>
                }
              >
                {(url) => (
                  <a href={url()} target="_blank" rel="noopener noreferrer">
                    <Button size="lg">
                      Join on Discord <ChevronRight size={18} />
                    </Button>
                  </a>
                )}
              </Show>
              <Show when={FEATURES.auth && inviteUrl}>
                <Button size="lg" variant="secondary"
                  onClick={signIn}
                >
                  Log in <ChevronRight size={18} />
                </Button>
              </Show>
            </>
          }
        >
          <Button
            size="lg"
            onClick={() => {
              const route = firstEnabledRoute(state.user!.username);
              if (route) navigate({ to: route });
            }}
          >
            Go to Dashboard <ChevronRight size={18} />
          </Button>
        </Show>
      </Show>
      <InstallButton variant="ghost" size="sm" />
    </div>
  );
}

function FeatureCard(props: {
  icon: Component<{ size?: number }>;
  title: string;
  desc: string;
}) {
  return (
    <div class="gleam-border group relative bg-surface-2 border border-brand/20 rounded-lg p-5 sm:p-8 h-full hover:border-brand/60 transition-all shadow-[0_0_15px_rgba(194,26,26,0.06)] hover:shadow-[0_0_25px_rgba(194,26,26,0.18)]">
      <div class="absolute inset-0 bg-gradient-to-br from-brand/10 to-transparent rounded-lg opacity-0 group-hover:opacity-100 transition-opacity" />
      <div class="relative z-10">
        <div class="text-brand mb-3 sm:mb-4">
          <props.icon size={32} />
        </div>
        <h3 class="font-display text-lg sm:text-xl font-bold text-text-base mb-2 sm:mb-3">
          {props.title}
        </h3>
        <p class="text-text-muted text-sm leading-relaxed">{props.desc}</p>
      </div>
    </div>
  );
}

function LandingPage() {
  const [ready, setReady] = createSignal(false);
  const [bracketOffset, setBracketOffset] = createSignal(0);

  const landingStats = createQuery(() => ({
    queryKey: [QueryKey.Landing],
    queryFn: () => landingApi.stats(),
    enabled: FEATURES.auth,
  }));

  // Flatten the pulse into ticker segments (label / value / when), dropping any
  // slot the server gated to null. Order reads last → next → total. New stats
  // slot in by pushing another segment, no layout change needed.
  const pulseSegments = (): { label: string; value: string; sub: string }[] => {
    const p = landingStats.data;
    // Tolerate an unexpected response shape - a client running an older cached
    // bundle can hit a changed API, and a LandingPulse that isn't a plain object
    // (e.g. a prior array shape) should degrade to no ticker, never throw.
    if (!p || typeof p !== "object" || Array.isArray(p)) return [];
    const segs: { label: string; value: string; sub: string }[] = [];
    if (p.heartbeat)
      segs.push({
        label: p.heartbeat.type === "duel" ? "Last duel" : "Last tournament",
        value:
          p.heartbeat.type === "duel"
            ? formatCategory(p.heartbeat.raceType, p.heartbeat.carClass)
            : p.heartbeat.name,
        sub: relTime(p.heartbeat.at),
      });
    if (p.anticipation)
      segs.push({
        label: "Next tournament",
        value: p.anticipation.name,
        sub: relTime(p.anticipation.at),
      });
    if (p.momentum)
      segs.push({
        label: "Total",
        value: `${p.momentum.count} ${p.momentum.count === 1 ? "event" : "events"}`,
        sub: "this month",
      });
    return segs;
  };

  onMount(() => {
    requestAnimationFrame(() => setReady(true));

    // Modulate streak/gleam speed via playbackRate
    const streakNames = new Set(["speed-line", "streak", "border-gleam"]);
    const getStreakAnimations = () =>
      document
        .getAnimations()
        .filter(
          (a) => a instanceof CSSAnimation && streakNames.has(a.animationName),
        );

    const onScroll = () => {
      const scrollFrac = Math.min(
        window.scrollY /
          (document.documentElement.scrollHeight - window.innerHeight),
        1,
      );
      // Fast at top (1.5×), slow at bottom (0.4×)
      const rate = 1.5 - scrollFrac * 1.1;
      for (const anim of getStreakAnimations()) {
        anim.playbackRate = rate;
      }
      // Move brackets into corners as user scrolls (0 → 200px)
      setBracketOffset(scrollFrac * 200);
    };
    window.addEventListener("scroll", onScroll, { passive: true });
    onCleanup(() => window.removeEventListener("scroll", onScroll));
  });

  return (
    <div class="overflow-hidden">
      {" "}
      {/* Corner accents - fixed, drift into corners on scroll */}
      <div
        class="fixed w-10 h-10 sm:w-16 sm:h-16 border-t-2 border-l-2 border-brand/40 opacity-60 pointer-events-none z-50 transition-all duration-10"
        style={{
          top: `calc(1rem - ${bracketOffset()}px)`,
          left: `calc(1rem - ${bracketOffset()}px)`,
        }}
      />
      <div
        class="fixed w-10 h-10 sm:w-16 sm:h-16 border-b-2 border-r-2 border-brand/40 opacity-60 pointer-events-none z-50 transition-all duration-10"
        style={{
          bottom: `calc(1rem - ${bracketOffset()}px)`,
          right: `calc(1rem - ${bracketOffset()}px)`,
        }}
      />
      {/* ── Hero ──────────────────────────────────────────────────────────── */}
      <section class="relative min-h-[100dvh] flex items-center justify-center px-4 sm:px-6 py-12 sm:py-20">
        {/* Aggressive diagonal slashes */}
        <div class="absolute inset-0 overflow-hidden pointer-events-none">
          <div
            class="absolute -top-1/2 -left-1/4 w-[150%] h-[200%] opacity-[0.03]"
            style={SLASH_LEFT_STYLE}
          />
        </div>

        {/* Redline glow at top */}
        <div class="absolute top-0 left-0 right-0 h-[2px] bg-brand animate-redline-glow" />

        {/* Speed lines */}
        <SpeedLines />

        <div class="relative z-10 max-w-6xl mx-auto w-full">
          <div class="text-center mb-12">
            {/* Kanji - smashes in */}
            <HeroLogo ready={ready()} />

            {/* Tachometer bar */}
            <div class="flex justify-center mb-6">
              <div class="relative w-48 h-[2px] bg-surface-3 rounded-full overflow-hidden">
                <div
                  class="absolute inset-y-0 left-0 bg-gradient-to-r from-brand-dim to-brand rounded-full"
                  classList={{ "animate-tach-sweep": ready() }}
                  style={TACH_SWEEP_STYLE}
                />
              </div>
            </div>

            {/* Tagline - slides from right */}
            <p
              class="text-text-muted text-base sm:text-lg max-w-2xl mx-auto leading-relaxed px-2 opacity-0"
              classList={{ "animate-drift-in-right": ready() }}
              style={TAGLINE_DELAY_STYLE}
            >
              Competitive Forza Horizon 6 racing, fair and organized.
            </p>
            <p
              class="text-text-muted text-base sm:text-lg max-w-2xl mx-auto leading-relaxed px-2 opacity-0"
              classList={{ "animate-drift-in-right": ready() }}
              style={TAGLINE_DELAY_STYLE}
            >
              Meet up, earn your place, race and chill.
            </p>
          </div>

          {/* CTAs - Join is primary (the newcomer's door); Login is the smaller
              "already a member" path inside CtaButtons. */}
          <div
            class="flex flex-col gap-4 items-center justify-center mb-16 opacity-0"
            classList={{ "animate-slide-up": ready() }}
            style={CTA_DELAY_STYLE}
          >
            <CtaButtons />
          </div>

          {/* The scene — a live timing-board panel of recent and upcoming
              activity. Deliberately NOT the gleam-card chrome the sections below
              use, and built from a flat segment list so new stats slot in with no
              layout work. One Rajdhani type ramp (label / value / when) so it reads
              as a single readout, not three clashing fonts. Big enough to own the
              width. Gated server-side; hides when nothing is fresh. */}
          <Show when={FEATURES.auth && pulseSegments().length > 0}>
            <div
              class="max-w-3xl mx-auto opacity-0"
              classList={{ "animate-slide-up": ready() }}
              style={STATS_DELAY_STYLE}
            >
              <div class="relative rounded-xl border border-brand/25 bg-surface-2/70 shadow-[0_0_30px_rgba(194,26,26,0.08)] overflow-hidden">
                {/* top accent line + LIVE header */}
                <div class="absolute top-0 inset-x-0 h-px bg-gradient-to-r from-transparent via-brand/60 to-transparent" />
                <div class="flex items-baseline gap-2 px-3 sm:px-5 pt-2 sm:pt-4 pb-1 sm:pb-3 border-b border-border/50">
                  <LiveDot />
                  <span class="text-label-sm font-semibold uppercase tracking-display text-text-muted">
                    Live
                  </span>
                </div>
                <div class="flex flex-col sm:flex-row sm:items-stretch">
                  <For each={pulseSegments()}>
                    {(seg) => (
                      <div class="min-w-0 flex-1 p-3 sm:p-5 md:py-6 text-center border-t border-border/50 first:border-t-0 sm:border-t-0 sm:border-l sm:first:border-l-0">
                        <div class="text-label-xs sm:text-label-sm font-semibold uppercase tracking-display text-text-muted mb-1 sm:mb-2">
                          {seg.label}
                        </div>
                        <div class="text-2xl sm:text-3xl lg:text-4xl font-bold text-text-base truncate leading-tight">
                          {seg.value}
                        </div>
                        <div class="text-sm font-semibold text-brand mt-0.5 sm:mt-1.5">
                          {seg.sub}
                        </div>
                      </div>
                    )}
                  </For>
                </div>
              </div>
            </div>
          </Show>
        </div>
      </section>
      {/* ── Why CRG (the fix for broken online racing) ───────────────────────── */}
      <section class="relative py-20 px-4 overflow-hidden">
        <div class="relative max-w-6xl mx-auto">
          <h2 class="font-display text-2xl sm:text-4xl md:text-5xl font-bold text-center mb-3 sm:mb-4">
            Fair races, <span class="text-brand">earned ranks.</span>
          </h2>
          <p class="text-text-muted text-sm sm:text-base max-w-2xl mx-auto text-center leading-relaxed mb-10 sm:mb-16">
            Open lobbies are full of hacks, cheese builds, and drivers who
            rage-quit the second they lose. Kurotsumi answers every one of those
            with a judge on the race, a cap on the car, and a ladder you climb on
            skill.
          </p>
          <div class="grid grid-cols-1 md:grid-cols-3 gap-4 sm:gap-8">
            <For each={WHY}>
              {(w) => <FeatureCard icon={w.icon} title={w.title} desc={w.desc} />}
            </For>
          </div>
        </div>
      </section>
      {/* ── Steps ─────────────────────────────────────────────────────────── */}
      <section class="relative py-20 px-4 overflow-hidden">
        {/* Subtle diagonal lines */}
        <div
          class="absolute inset-0 opacity-[0.02] pointer-events-none"
          style={SLASH_RIGHT_STYLE}
        />
        <div class="relative max-w-6xl mx-auto">
          <h2 class="font-display text-2xl sm:text-4xl md:text-5xl font-bold text-center mb-10 sm:mb-16">
            How to rank up?
          </h2>
          {/* Stepper, not cards: Challenge/Race/Climb is a sequence, so connect
              the numbered nodes with a line. Reads as steps, and visually distinct
              from the parallel "fix" cards above. */}
          <div class="relative max-w-5xl mx-auto flex flex-col md:flex-row gap-10 md:gap-0">
            <For each={STEPS}>
              {(s, i) => (
                <div class="relative flex-1 text-center px-4">
                  <Show when={i() > 0}>
                    <div class="hidden md:block absolute top-7 left-0 right-1/2 h-px bg-gradient-to-r from-brand/0 to-brand/40" />
                  </Show>
                  <Show when={i() < STEPS.length - 1}>
                    <div class="hidden md:block absolute top-7 left-1/2 right-0 h-px bg-gradient-to-r from-brand/40 to-brand/0" />
                  </Show>
                  <div class="relative z-10 mx-auto mb-4 flex h-14 w-14 items-center justify-center rounded-full border-2 border-brand/40 bg-surface-2 font-display text-2xl font-bold text-brand shadow-[0_0_20px_rgba(194,26,26,0.15)]">
                    {s.n}
                  </div>
                  <h3 class="font-display text-lg sm:text-xl font-bold text-text-base mb-2">
                    {s.title}
                  </h3>
                  <p class="text-text-muted text-sm leading-relaxed max-w-xs mx-auto">
                    {s.desc}
                  </p>
                </div>
              )}
            </For>
          </div>
        </div>
      </section>
      {/* ── The crew (community, not just competition) ───────────────────────── */}
      <section class="relative py-16 sm:py-20 px-4 sm:px-6 overflow-hidden">
        <div class="relative max-w-2xl mx-auto text-center space-y-5">
          <h2 class="font-display text-2xl sm:text-4xl font-bold text-text-base">
            <span class="text-brand">More</span> than the racing.
          </h2>
          <p class="text-text-muted text-sm sm:text-base leading-relaxed">
            Win or lose, every event starts with a car meet. We trade
            tried-and-tested tunes, run regular livery competitions, and show off
            the builds and photos we're proud of. Tournaments are streamed with
            live commentary from the judges.
          </p>
          <p class="text-text-muted text-sm sm:text-base leading-relaxed">
            No money ever changes hands, toxicity gets you removed, and you have
            to be 18 or older. Bring a car you built yourself and come settle it
            on the track.
          </p>
        </div>
      </section>
      {/* ── Tier strip separator ───────────────────────────────────────────── */}
      <div class="relative py-6 sm:py-10 px-4 border-y border-brand/20 overflow-hidden">
        <p class="font-display text-lg sm:text-xl font-bold text-text-base text-center mb-2">
          Ten tiers, white to black
        </p>
        <p class="text-text-muted leading-relaxed text-sm max-w-md mx-auto text-center mb-4">
          Your tier is set by the group through judged races, earned on the
          track. Everyone starts at white and climbs.
        </p>
        <TierStreaks />
        <div class="relative max-w-6xl mx-auto flex flex-wrap justify-center gap-2">
          <For each={TIERS}>{(tier) => <TierBadge tier={tier} />}</For>
        </div>
      </div>
      {/* ── Bottom CTA ────────────────────────────────────────────────────── */}
      <section class="relative py-12 sm:py-20 px-4 sm:px-6 overflow-hidden">
        {/* Background glow */}
        <div class="absolute inset-0 flex items-center justify-center pointer-events-none">
          <div class="w-64 sm:w-96 h-64 sm:h-96 bg-brand/8 rounded-full blur-[100px] sm:blur-[120px]" />
        </div>
        <div class="relative max-w-2xl mx-auto text-center px-2">
          <h2 class="font-display text-2xl sm:text-4xl font-bold text-text-base mb-4 sm:mb-6">
            Can you <span class="text-brand">keep up</span>?
          </h2>
          <p class="text-text-muted text-sm sm:text-base mb-6 sm:mb-8 leading-relaxed">
            Kurotsumi runs on Discord. Jump in, build a car, and call out your
            first rival. You start at white tier like everyone else.
          </p>
          <CtaButtons />
        </div>
      </section>
    </div>
  );
}

function HeroLogo(props: { ready: boolean }) {
  return (
    <div
      class="flex flex-col items-center justify-center mb-6 opacity-0"
      classList={{ "animate-rev-up": props.ready }}
    >
      <div class="relative w-48 h-48">
        <img
          src={logoUrl}
          alt="黒罪"
          class="min-w-256 absolute -top-100 -left-15"
        />
      </div>
      <img
        src={headerLogoUrl}
        alt="Kurotsumi CRG"
        class="w-128 relative z-2"
      />
    </div>
  );
}
